/**
 * Message parsing utilities for structured content extraction
 */

import type { SelectionRequest } from '@/types/command-selection';

/**
 * Clean JSON input by removing code block markers and parsing nested JSON strings
 */
function cleanJsonInput(input: any): any {
    if (!input || typeof input !== 'object') {
        return input;
    }

    if (Array.isArray(input)) {
        return input.map(item => cleanJsonInput(item));
    }

    const cleaned: any = {};

    for (const [key, value] of Object.entries(input)) {
        if (typeof value === 'string') {
            const trimmed = value.trim();
            const hasBackticks = trimmed.includes('```');
            const looksLikeJson = (trimmed.startsWith('{') || trimmed.startsWith('[')) &&
                (trimmed.endsWith('}') || trimmed.endsWith(']') || hasBackticks);

            if (hasBackticks || looksLikeJson) {
                try {
                    const cleanedStr = trimmed
                        .replace(/\n*```+\s*$/g, '')
                        .replace(/^```+[a-z]*\n*/g, '')
                        .trim();

                    let parsed: any = cleanedStr;
                    let attempts = 0;

                    while (typeof parsed === 'string' && attempts < 3) {
                        try {
                            parsed = JSON.parse(parsed);
                            attempts++;
                        } catch {
                            break;
                        }
                    }

                    cleaned[key] = parsed;
                } catch {
                    cleaned[key] = value;
                }
            } else {
                cleaned[key] = value;
            }
        } else if (typeof value === 'object' && value !== null) {
            cleaned[key] = cleanJsonInput(value);
        } else {
            cleaned[key] = value;
        }
    }

    return cleaned;
}

export interface ParsedTool {
    toolCallId: string;
    type: `tool-${string}`;
    state: 'input-streaming' | 'input-available' | 'output-available' | 'output-error';
    input: any;
    output?: string;
    errorText?: string;
    position?: number;
}

export interface ContentSegment {
    type: 'text' | 'tool' | 'error' | 'selection';
    content?: string;
    tool?: ParsedTool;
    selection?: SelectionRequest;
}

export interface ParsedContent {
    content: string;
    reasoning: string;
    tools: ParsedTool[];
    contentSegments: ContentSegment[];
    rawContent: string;
}

interface ContentMarker {
    type: 'thought' | 'action' | 'final-answer' | 'text';
    index: number;
    endIndex: number;
    content?: string;
    tool?: ParsedTool;
}

/**
 * Extract reasoning/thoughts from content
 */
function extractReasoning(rawContent: string, isStreaming: boolean): string {
    const thoughtPattern = /Thought:\s*((?:(?!\n(?:Action|Final Answer):)[\s\S])+?)(?=\n(?:Action|Final Answer):|$)/gi;

    if (isStreaming) {
        // During streaming: only extract complete thoughts
        const completeThoughtPattern = /Thought:\s*([\s\S]+?)(?=\n(?:Action|Final Answer):)/gi;
        const completeThoughts: string[] = [];
        let match;

        while ((match = completeThoughtPattern.exec(rawContent)) !== null) {
            const thoughtText = match[1].trim();
            if (thoughtText) {
                completeThoughts.push(thoughtText);
            }
        }

        return completeThoughts.join('\n\n');
    }

    // After streaming: extract all thoughts
    const thoughts: string[] = [];
    let match;

    while ((match = thoughtPattern.exec(rawContent)) !== null) {
        const thoughtText = match[1].trim();
        if (thoughtText) {
            thoughts.push(thoughtText);
        }
    }

    return thoughts.join('\n\n');
}

/**
 * Format observation output (parse JSON if possible)
 */
function formatObservation(observation: string): string {
    try {
        const trimmedObs = observation.trim();
        if (!trimmedObs.startsWith('{') && !trimmedObs.startsWith("'") && !trimmedObs.startsWith('"')) {
            return observation;
        }

        let jsonStr = trimmedObs;

        // Remove outer quotes if present
        if ((jsonStr.startsWith("'") && jsonStr.endsWith("'")) ||
            (jsonStr.startsWith('"') && jsonStr.endsWith('"'))) {
            jsonStr = jsonStr.slice(1, -1);
        }

        // Handle Python dict syntax
        jsonStr = jsonStr
            .replace(/\bNone\b/g, 'null')
            .replace(/\bTrue\b/g, 'true')
            .replace(/\bFalse\b/g, 'false');

        let parsed;
        try {
            parsed = JSON.parse(jsonStr);
        } catch {
            jsonStr = jsonStr.replace(/'/g, '"');
            parsed = JSON.parse(jsonStr);
        }

        if (typeof parsed === 'object' && parsed !== null) {
            const formattedJson = JSON.stringify(parsed, null, 2);
            return `\`\`\`json\n${formattedJson}\n\`\`\``;
        }
    } catch {
        // If parsing fails, return original
    }

    return observation;
}

/**
 * Clean observation text by removing code blocks with structured content
 */
function cleanObservation(observation: string): string {
    let cleaned = observation
        .replace(/```[\s\S]*?```/g, (match) => {
            // Remove code blocks containing Thought or Final Answer
            if (match.match(/Thought:|Final Answer:/i)) {
                return '';
            }
            return match;
        })
        .trim();

    // Remove standalone Thought/Final Answer lines
    cleaned = cleaned
        .replace(/Thought:\s*[\s\S]+?(?=\n\n|$)/gi, '')
        .replace(/Final Answer:\s*[\s\S]+?(?=\n\n|$)/gi, '')
        .trim();

    return cleaned;
}

/**
 * Parse action blocks with observations
 */
function parseActionBlocks(rawContent: string, idScope: string): { markers: ContentMarker[]; tools: ParsedTool[] } {
    const markers: ContentMarker[] = [];
    const tools: ParsedTool[] = [];
    const actionBlockPattern = /Action:\s*([^\n]+)\nAction Input:\s*([\s\S]+?)\nObservation:\s*([\s\S]+?)(?=\n\n(?:Action|Thought|Final Answer):|\n+```|$)/gi;
    let toolIndex = 0;
    let match;

    while ((match = actionBlockPattern.exec(rawContent)) !== null) {
        const actionName = match[1].trim();
        const actionInput = match[2].trim();
        let observation = match[3].trim();

        observation = cleanObservation(observation);
        const formattedObservation = formatObservation(observation);

        let parsedInput: any;
        try {
            parsedInput = JSON.parse(actionInput);
        } catch {
            parsedInput = actionInput;
        }

        // Clean the parsed input to remove code block markers
        const cleanedInput = cleanJsonInput(parsedInput);

        const tool: ParsedTool = {
            toolCallId: `tool-${idScope}-action-${match.index}-${toolIndex++}`,
            type: `tool-${actionName.toLowerCase().replace(/\s+/g, '_')}`,
            state: 'output-available',
            input: cleanedInput,
            output: formattedObservation,
            errorText: undefined,
            position: tools.length,
        };

        tools.push(tool);

        const fullMatch = match[0];
        const observationStartOffset = fullMatch.indexOf('Observation:');
        const observationStartIndex = match.index + observationStartOffset;
        const observationTextLength = observation.trim().length;
        const actualObservationEnd = observationStartIndex + 'Observation:'.length + observationTextLength;

        markers.push({
            type: 'action',
            index: match.index,
            endIndex: actualObservationEnd,
            tool: tool,
        });
    }

    return { markers, tools };
}

/**
 * Parse action blocks without observations (streaming)
 */
function parseStreamingActions(
    rawContent: string,
    processedIndices: Set<number>,
    idScope: string
): { markers: ContentMarker[]; tools: ParsedTool[] } {
    const markers: ContentMarker[] = [];
    const tools: ParsedTool[] = [];
    const actionOnlyPattern = /Action:\s*([^\n]+)\nAction Input:\s*([\s\S]+?)(?=\nObservation:|$)/gi;
    let toolIndex = 0;
    let match;

    while ((match = actionOnlyPattern.exec(rawContent)) !== null) {
        if (processedIndices.has(match.index)) continue;

        const actionName = match[1].trim();
        const actionInput = match[2].trim();

        let parsedInput: any;
        try {
            parsedInput = JSON.parse(actionInput);
        } catch {
            parsedInput = actionInput;
        }

        // Clean the parsed input to remove code block markers
        const cleanedInput = cleanJsonInput(parsedInput);

        const tool: ParsedTool = {
            toolCallId: `tool-${idScope}-stream-${match.index}-${toolIndex++}`,
            type: `tool-${actionName.toLowerCase().replace(/\s+/g, '_')}`,
            state: 'input-available',
            input: cleanedInput,
            output: undefined,
            errorText: undefined,
            position: tools.length,
        };

        tools.push(tool);
        markers.push({
            type: 'action',
            index: match.index,
            endIndex: match.index + match[0].length,
            tool: tool,
        });
    }

    return { markers, tools };
}

/**
 * Check if Final Answer is inside a code block
 */
function isInsideCodeBlock(rawContent: string, finalAnswerIndex: number): boolean {
    const beforeMatch = rawContent.substring(0, finalAnswerIndex);
    const codeBlockMatches = [...beforeMatch.matchAll(/```+/g)];
    const codeBlockCount = codeBlockMatches.length;

    const contextBefore = rawContent.substring(Math.max(0, finalAnswerIndex - 300), finalAnswerIndex);

    // Check for various patterns indicating we're after a closing code block
    const patterns = [
        /```+\s*\n+\s*(?:Thought:\s*[^\n]+\s*\n+\s*)?Final Answer:/i,
        /```+\s*\n+\s*Final Answer:/i,
        /```+[\s\S]*?Thought:\s*[^\n]+\s*\n+\s*Final Answer:/i,
        /Observation:[\s\S]*?```+\s*\n+\s*Thought:\s*[^\n]+\s*\n+\s*Final Answer:/i,
    ];

    const isAfterClosing = patterns.some(pattern => pattern.test(contextBefore));
    const isInsideByCount = codeBlockCount % 2 !== 0;

    return isInsideByCount && !isAfterClosing;
}

/**
 * Clean final answer content
 */
function cleanFinalAnswer(finalAnswerContent: string): string {
    return finalAnswerContent;
}

/**
 * Parse final answer sections
 */
function parseFinalAnswers(
    rawContent: string,
    actionBlockIndices: Array<{ start: number; end: number }>
): ContentMarker[] {
    const markers: ContentMarker[] = [];
    const finalAnswerPattern = /Final Answer:\s*([\s\S]+?)(?=\n\n(?:Thought|Action|Final Answer):|\n(?:Thought|Action|Final Answer):|$)/gi;
    let match;

    while ((match = finalAnswerPattern.exec(rawContent)) !== null) {
        const finalAnswerIndex = match.index;

        // Skip if inside an action block
        const isInsideActionBlock = actionBlockIndices.some(block =>
            finalAnswerIndex >= block.start && finalAnswerIndex < block.end
        );
        if (isInsideActionBlock || isInsideCodeBlock(rawContent, finalAnswerIndex)) {
            continue;
        }

        const finalAnswerContent = cleanFinalAnswer(match[1]);

        if (finalAnswerContent && finalAnswerContent.length > 0) {
            markers.push({
                type: 'final-answer',
                index: match.index,
                endIndex: match.index + match[0].length,
                content: finalAnswerContent,
            });
        }
    }

    return markers;
}

/**
 * Parse thought markers
 */
function parseThoughts(rawContent: string): ContentMarker[] {
    const markers: ContentMarker[] = [];
    const thoughtPattern = /Thought:\s*((?:(?!\n(?:Action|Final Answer):)[\s\S])+?)(?=\n(?:Action|Final Answer):|$)/gi;
    let match;

    while ((match = thoughtPattern.exec(rawContent)) !== null) {
        markers.push({
            type: 'thought',
            index: match.index,
            endIndex: match.index + match[0].length,
        });
    }

    return markers;
}

/**
 * Build content segments from markers
 */
function buildContentSegments(
    rawContent: string,
    markers: ContentMarker[],
    lastFinalAnswer: ContentMarker | null,
    isStreaming: boolean
): ContentSegment[] {
    const contentSegments: ContentSegment[] = [];

    // Add text at the beginning
    if (markers.length > 0 && markers[0].index > 0) {
        const textAtStart = cleanText(rawContent.substring(0, markers[0].index));
        if (shouldAddTextSegment(textAtStart, lastFinalAnswer, markers[0].index, isStreaming)) {
            contentSegments.push({ type: 'text', content: textAtStart });
        }
    }

    // Process markers
    for (let i = 0; i < markers.length; i++) {
        const marker = markers[i];
        const nextMarker = i < markers.length - 1 ? markers[i + 1] : null;

        // Add tool
        if (marker.type === 'action' && marker.tool) {
            if (isStreaming || !lastFinalAnswer || marker.index < lastFinalAnswer.index) {
                contentSegments.push({ type: 'tool', tool: marker.tool });
            }
        }

        // Add text between markers
        if (marker.type !== 'thought' && nextMarker) {
            const textBetween = rawContent.substring(marker.endIndex, nextMarker.index);
            const cleaned = cleanText(textBetween);
            if (shouldAddTextSegment(cleaned, lastFinalAnswer, marker.index, isStreaming)) {
                contentSegments.push({ type: 'text', content: cleaned });
            }
        }
    }

    // Add final answer
    if (lastFinalAnswer && lastFinalAnswer.content && lastFinalAnswer.content.length > 0) {
        contentSegments.push({ type: 'text', content: lastFinalAnswer.content });
    }

    return contentSegments;
}

/**
 * Clean text by removing code blocks and structured markers
 */
function cleanText(text: string): string {
    return text;
}

/**
 * Check if text segment should be added
 */
function shouldAddTextSegment(
    text: string,
    lastFinalAnswer: ContentMarker | null,
    markerIndex: number,
    isStreaming: boolean
): boolean {
    if (!text || text.length <= 1) return false;
    if (text.match(/^[`\s\n\.\{\}\[\]]+$/)) return false;
    if (text.match(/^(Action|Action Input|Observation|Thought|Final Answer):/i)) return false;

    const isBeforeFinalAnswer = !lastFinalAnswer || markerIndex < lastFinalAnswer.index;
    return isStreaming || isBeforeFinalAnswer;
}

/**
 * Main parsing function
 */
export function parseStructuredContent(rawContent: string, isStreaming: boolean = false, idScope: string = 'msg'): ParsedContent {
    const reasoning = extractReasoning(rawContent, isStreaming);
    const allMarkers: ContentMarker[] = [];
    const allTools: ParsedTool[] = [];

    // Parse action blocks with observations
    const { markers: actionMarkers, tools: actionTools } = parseActionBlocks(rawContent, idScope);
    allMarkers.push(...actionMarkers);
    allTools.push(...actionTools);

    // Parse streaming actions (without observations)
    const processedIndices = new Set(actionMarkers.map(m => m.index));
    const { markers: streamingMarkers, tools: streamingTools } = parseStreamingActions(rawContent, processedIndices, idScope);
    allMarkers.push(...streamingMarkers);
    allTools.push(...streamingTools);

    // Parse final answers
    const actionBlockIndices = actionMarkers.map(m => ({ start: m.index, end: m.endIndex }));
    const finalAnswerMarkers = parseFinalAnswers(rawContent, actionBlockIndices);
    allMarkers.push(...finalAnswerMarkers);

    // Parse thoughts (for marker boundaries)
    const thoughtMarkers = parseThoughts(rawContent);
    allMarkers.push(...thoughtMarkers);

    // Sort markers by position
    allMarkers.sort((a, b) => a.index - b.index);

    // Find last final answer
    const lastFinalAnswer = finalAnswerMarkers.length > 0
        ? finalAnswerMarkers[finalAnswerMarkers.length - 1]
        : null;

    // Build content segments
    const contentSegments = buildContentSegments(rawContent, allMarkers, lastFinalAnswer, isStreaming);

    // Build clean content
    let cleanContent = '';
    if (lastFinalAnswer && lastFinalAnswer.content && lastFinalAnswer.content.length > 0) {
        cleanContent = lastFinalAnswer.content;
    }

    return {
        content: cleanContent,
        reasoning: reasoning,
        tools: allTools,
        contentSegments: contentSegments.length > 0 ? contentSegments : (cleanContent ? [{ type: 'text', content: cleanContent }] : []),
        rawContent: rawContent,
    };
}

